import { type FC, useState, useEffect, useCallback } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy.js';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers.ts';
import { trackPageview, trackUserEventHomepageView } from 'dibs-tracking';
import { mobile } from 'dibs-client-check';
import { useIntl } from 'dibs-react-intl';
import { useHomePageContext } from './HomePageContext.tsx';
import { HpSharedAppBannerLazy } from './HpSharedAppBanner/HpSharedAppBannerLazy.tsx';
import { HpSharedCompleteEmailOptinConfirmationModalLazy } from './HpSharedCompleteEmailOptinConfirmationModal/HpSharedCompleteEmailOptinConfirmationModalLazy.tsx';
import { getUserZipCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { componentModuleMap } from './componentModuleMap.tsx';
import { HpSharedBillboardAd } from './HpSharedBillboardAd/HpSharedBillboardAd.tsx';
import {
    type DeviceType,
    MOBILE,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { HpSharedRecentlyViewedItemsModule } from './HpSharedRecentlyViewedItems/HpSharedRecentlyViewedItemsModule.tsx';
import { HpRecentlySearchedItemsModuleLazy } from './HpRecentlySearchedItems/HpRecentlySearchedItemsModuleLazy.tsx';
import {
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    HOME_TOP_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants.ts';

import { type HpSharedLayout_viewer$data } from './__generated__/HpSharedLayout_viewer.graphql.ts';
import HpSharedSkinnyBannerModule from './HpSharedSkinnyBannerModule/HpSharedSkinnyBannerModule.tsx';
import { HpSharedFeaturedModule } from './HpSharedFeaturedModule/HpSharedFeaturedModule.tsx';
import { HpSharedNewArrivalsCarouselTabs } from './HpSharedNewArrivalsCarouselTabs/HpSharedNewArrivalsCarouselTabs.tsx';

import { trackRemoveTopHeroBannerTest } from './helpers/removeTopHeroBannerTestHelpers.ts';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { HpSharedUserRecommendations } from './HpSharedUserRecommendations/HpSharedUserRecommendations.tsx';
import { trackExpand2TowerRecsTest } from './helpers/expand2TowerRecsTestHelpers.ts';
import {
    getUserRecommendationType,
    trackSiglipRecsTest,
} from 'dibs-personalized/exports/siglipRecsTestHelpers.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { HpSharedItemsGrid } from './HpSharedItemsGrid/HpSharedItemsGrid.tsx';

const HpSharedRecentlyFavoritedCarouselLazy = dibsLazy(
    'HpSharedRecentlyFavoritedCarousel',
    () =>
        import(
            /* webpackChunkName: "HpSharedRecentlyFavoritedCarousel" */ './HpSharedRecentlyFavoritedCarousel/HpSharedRecentlyFavoritedCarousel.tsx'
        )
);

type HpSharedLayoutProps = {
    relay: RelayRefetchProp;
    viewer: HpSharedLayout_viewer$data;
};

const HpSharedLayoutComponent: FC<HpSharedLayoutProps> = ({ relay, viewer }) => {
    const intl = useIntl();
    const [userId, setUserId] = useState<string | null>(null);
    const [userType, setUserType] = useState<string | null>(null);
    const [isClient, setIsClient] = useState<boolean>(false);
    const [fetchFavorites, setFetchFavorites] = useState(false);

    const {
        isMobile,
        isTablet,
        isLoggedInUser,
        isTopDisplayAdEnabled,
        isBottomDisplayAdEnabled,
        userType: userTypeFromSever,
    } = useHomePageContext();

    let deviceType: DeviceType = DESKTOP;
    if (isMobile) {
        deviceType = MOBILE;
    } else if (isTablet) {
        deviceType = TABLET;
    }

    const user = viewer?.user || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userType: userType || userTypeFromSever || '',
                    zipCode: getUserZipCode(),
                    recommendationType: getUserRecommendationType(),
                    ...additionalVariables,
                }),
                { fetchInitialHomepage: true },
                () => {
                    setIsClient(true);

                    // ensure that we are done with refetch before considering to allow favorites fetch
                    if (userId) {
                        setFetchFavorites(true);
                    }
                }
            );
        },
        [userId, userType, userTypeFromSever, relay, isMobile]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            // order is important to prevent unnecessary refetch, read more: https://github.com/1stdibs/ferrum/pull/13601#pullrequestreview-818832289
            setUserType(getUserType(document.cookie));
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        trackRemoveTopHeroBannerTest();
    }, []);

    useEffect(() => {
        trackPageview({ additional: { opt_pageURL: '/' } });
    }, []);

    useEffect(() => {
        trackUserEventHomepageView(relay.environment);
    }, [relay.environment]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        setUserData();
        addLoginHandlers((data: { user: { isVerifiedTrade?: boolean | null } }) => {
            if (data?.user?.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload();
                return;
            }
            setUserData();
        });
    }, [setUserData, intl.locale]);

    useDwelledOnPage(PAGE_TYPE.HOME);

    const { homepage } = viewer;

    const isUser = isLoggedInUser || !!userId; // isLoggedInUser comes at load; userId might come later from refetch

    const { personalizationCollections, modules } = homepage || {};

    const hasLoggedInWithApp = user?.hasLoggedInWithApp;

    let editorialIndex = 1;

    const mods = (modules || []).map((mod, index) => {
        if (!mod) {
            return null;
        }
        const type = mod.type;

        if (!type) {
            return null;
        }

        const component = componentModuleMap[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                viewer,
                isMobile,
                user,
                isClient,
                editorialModuleOrder: type === 'editorial' ? editorialIndex++ : 0,
            });
        }
        return null;
    });

    const getModuleIndex = ({
        moduleKey,
        direction = 'forward',
    }: {
        moduleKey: string;
        direction?: 'forward' | 'backward';
    }): number => {
        const moduleIndex =
            direction === 'forward'
                ? mods.findIndex(mod => (mod?.key || '').toString().includes(moduleKey))
                : mods.findLastIndex(mod => (mod?.key || '').toString().includes(moduleKey));

        if (moduleIndex === -1) {
            return mods.length - 1;
        }

        return moduleIndex;
    };

    /**
     * non cms controlled modules insertion
     */
    mods.splice(
        0,
        0,
        <HpSharedRecentlyViewedItemsModule
            key="hp-recentlyViewed"
            moduleIndex={1}
            totalModules={modules?.length || 0}
            deviceType={deviceType}
        />
    );

    if (isUser) {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial' }) + 1,
            0,
            <HpSharedNewArrivalsCarouselTabs
                key="hp-new-arrivals-carousel-tabs"
                viewer={viewer}
                user={user || null}
                personalizationCollections={personalizationCollections?.filter(filterFalsy) || null}
            />
        );
    }

    const recentlySearchedInsertIndex = isUser
        ? getModuleIndex({ moduleKey: 'hp-new-arrivals-carousel-tabs' }) + 1
        : getModuleIndex({ moduleKey: 'editorial' }) + 1;
    mods.splice(
        recentlySearchedInsertIndex,
        0,
        <HpRecentlySearchedItemsModuleLazy key="hp-recentlySearched" />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'hp-recentlySearched' }) + 1,
        0,
        <HpSharedItemsGrid
            key={`hp-shared-items-grid${userId ? `-${userId}` : ''}`}
            user={user}
            userId={userId}
            fetchFavorites={fetchFavorites}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'locations' }) + 1,
        0,
        <HpSharedFeaturedModule
            key="featured"
            viewer={isClient ? viewer : null}
            user={user}
            isClient={isClient}
        />
    );

    const expand2TowerRecsAbTestTracker = (
        <VisibilityTracker
            key="hp-expand-2tower-recs-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackExpand2TowerRecsTest();
                }
            }}
        />
    );

    const siglipRecsAbTestTracker = (
        <VisibilityTracker
            key="hp-siglip-recs-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackSiglipRecsTest();
                }
            }}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'editorial' }) + 1,
        0,
        expand2TowerRecsAbTestTracker,
        siglipRecsAbTestTracker,
        <HpSharedUserRecommendations key="hp-user-recommendations" user={user} />
    );

    if (isClient) {
        mods.splice(
            getModuleIndex({ moduleKey: 'featured' }) + 2,
            0,
            <HpSharedRecentlyFavoritedCarouselLazy
                key="hp-recently-saved"
                userId={userId || ''}
                isTrade={userType === TRADE}
                priceBookName={''}
            />
        );
    }

    if (isTopDisplayAdEnabled) {
        mods.splice(
            1, // insert after the recently viewed items module
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-top"
                placement={HOME_TOP_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (isBottomDisplayAdEnabled) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-bottom"
                placement={HOME_BOTTOM_BILLBOARD_PLACEMENT}
            />
        );
    }

    return (
        <>
            {mods}
            <HpSharedSkinnyBannerModule />
            {isClient && <HpSharedCompleteEmailOptinConfirmationModalLazy />}
            {isClient &&
                (mobile.iPhone() || mobile.iPad()) &&
                !(isLoggedInUser && hasLoggedInWithApp) && <HpSharedAppBannerLazy user={user} />}
        </>
    );
};

export const HpSharedLayout = createRefetchContainer(
    HpSharedLayoutComponent,
    {
        viewer: graphql`
            fragment HpSharedLayout_viewer on Viewer
            @argumentDefinitions(
                recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
            ) {
                homepage(rootId: $homepageId, abTestList: $abTestList) {
                    modules {
                        ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                            # don't skip this module on refetch, otherwise the dibs-carousel within
                            # won't get any items
                            ... on TopHeroBannerModule {
                                type
                                ...HpSharedTopHeroBannerModule_componentModule
                            }
                        }

                        ... on EditorialModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpSharedEditorialModule_componentModule
                        }
                        ... on LocationsModule {
                            type
                        }
                        ... on BottomBannerModule {
                            type
                            # visible used in componentModuleMap to check if the module is visible
                            # eslint-disable-next-line relay/unused-fields
                            visible
                            ...HpSharedFeaturedBannerModule_componentModule
                        }
                    }
                    personalizationCollections(userId: $userId, numItems: 10, pageSize: 3)
                        @include(if: $hasUserId) {
                        ...HpSharedNewArrivalsCarouselTabs_personalizationCollections
                    }
                }
                ...HpSharedNewArrivalsTabContent_viewer
                ...HpSharedFeaturedModule_viewer @include(if: $isClient)
                ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                    ...HpSharedTopHeroBannerModule_viewer
                }

                user(userId: $userId) @include(if: $hasUserId) {
                    hasLoggedInWithApp: hasLoggedInWithDeviceType(deviceType: IOS_APP)
                    ...HpSharedNewArrivalsTabContent_user

                    ...HpSharedFeaturedModule_user
                    ...HpSharedAppBanner_user
                    ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                        ...HpSharedTopHeroBannerModule_user
                    }
                    ...HpSharedUserRecommendations_user
                        @arguments(recommendationType: $recommendationType)
                    ...HpSharedItemsGrid_user @arguments(recommendationType: $recommendationType)
                }
            }
        `,
    },
    graphql`
        query HpSharedLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $userType: String!
            $abTestList: [String] = []
            $isClient: Boolean!
            $isRemoveTopHeroBannerTestVariant: Boolean!
            $recommendationType: UserRecommendationType!
        ) {
            viewer {
                ...HpSharedLayout_viewer @arguments(recommendationType: $recommendationType)
            }
        }
    `
);
